import React, { useState } from "react";
import { Cloud, Shield, Lock, Zap, FileSearch, Eye } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Marketing() {
  const [form, setForm] = useState({
    email: "", desired_slug: "", company_name: "", plan: "starter", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/signup-requests", form);
      setSubmitted(true);
      toast.success("Signup received — we'll provision your portal shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-zinc-800 bg-zinc-900/60 rounded-sm p-8 fade-up text-center space-y-4">
          <div className="font-display text-3xl font-bold tracking-tight">Request received.</div>
          <div className="text-zinc-400 text-sm">
            Your portal will be live at{" "}
            <span className="text-cyan-400 font-mono">{form.desired_slug}.rustwall.io</span> after
            review. We'll email <span className="text-cyan-400 font-mono">{form.email}</span> with the
            admin password and login URL.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-8">
            <img src="/rustwall-logo.png" alt="Rustwall" className="h-10 w-auto object-contain" />
          </div>
          <h1 className="font-display text-5xl xl:text-7xl font-black tracking-tight leading-[1.05] max-w-4xl">
            Multi-cloud firewall control,
            <br />
            <span className="text-cyan-400">re-architected in Rust.</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mt-6 font-mono text-sm leading-relaxed">
            POSTURE // RUNTIME // POLICY // DLP // PROXY // AUDIT — unified across AWS, Azure, GCP,
            Oracle, Cloudflare, VirtualExpanse, and Omnissa. Every customer gets their own isolated
            management portal at{" "}
            <span className="text-cyan-400">&lt;your-org&gt;.rustwall.io</span>.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { i: Cloud, t: "Multi-cloud posture", s: "CSPM across 7 providers, one canonical rule model" },
            { i: Shield, t: "Runtime detection", s: "Live threat surfacing with per-tenant audit" },
            { i: Lock, t: "Zero-trust policy", s: "Identity-aware enforcement on every policy" },
            { i: FileSearch, t: "DLP + labels", s: "Inline scan with label-aware rules" },
            { i: Zap, t: "Self-healing", s: "Auto-remediation when drift is detected" },
            { i: Eye, t: "Compliance maps", s: "SOC 2 · NIST · PCI · HIPAA · CIS" },
          ].map(({ i: I, t, s }) => (
            <div key={t} className="border border-zinc-800 bg-zinc-900/60 rounded-sm p-5 fade-up">
              <I className="w-5 h-5 text-cyan-400 mb-3" />
              <div className="font-display font-bold text-base tracking-tight">{t}</div>
              <div className="text-zinc-400 text-xs font-mono mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Signup */}
      <section className="border-t border-zinc-800 bg-zinc-900/40">
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">
            // PROVISION YOUR PORTAL
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-2">
            Request your tenant.
          </h2>
          <p className="text-zinc-400 text-sm mb-8 font-mono">
            We'll review and provision a dedicated stack on a private VM with its own backend,
            database, and tunnel. No shared infrastructure.
          </p>

          <form onSubmit={submit} className="space-y-4 fade-up" data-testid="signup-form">
            <Field label="Work email">
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ops@acme.com"
                data-testid="signup-email"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono focus:outline-none focus:border-cyan-500"
              />
            </Field>

            <Field label="Desired subdomain (lowercase, 3-32 chars)">
              <div className="flex items-center border border-zinc-800 rounded-sm bg-zinc-950 focus-within:border-cyan-500">
                <input
                  type="text" required value={form.desired_slug}
                  onChange={(e) => setForm({ ...form, desired_slug: e.target.value.toLowerCase() })}
                  pattern="^[a-z][a-z0-9-]{1,30}[a-z0-9]$"
                  placeholder="acme"
                  data-testid="signup-slug"
                  className="flex-1 px-3 py-2 bg-transparent text-sm font-mono focus:outline-none"
                />
                <div className="px-3 py-2 text-zinc-500 text-sm font-mono border-l border-zinc-800">
                  .rustwall.io
                </div>
              </div>
            </Field>

            <Field label="Company name">
              <input
                type="text" value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Acme Corp"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono focus:outline-none focus:border-cyan-500"
              />
            </Field>

            <Field label="Plan">
              <select
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono focus:outline-none focus:border-cyan-500"
              >
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </Field>

            <Field label="Anything else (optional)">
              <textarea
                rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Compliance, deployment region, integrations…"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono focus:outline-none focus:border-cyan-500"
              />
            </Field>

            <button
              type="submit" disabled={busy}
              data-testid="signup-submit"
              className="w-full px-4 py-3 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-zinc-950 rounded-sm font-display font-bold tracking-tight"
            >
              {busy ? "SUBMITTING…" : "REQUEST PORTAL"}
            </button>
          </form>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-6 text-center">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600">
          rustwall · multi-cloud firewall control · v3.2.1
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{label}</div>
      {children}
    </label>
  );
}
